import React, { useState, useEffect, useCallback } from 'react';
import styles from './AmenityLightbox.module.css';
import LeftChevron from '../icons/LeftChevron';
import CrossIcon from '../icons/CrossIcon';

export interface MediaFile {
  url: string;
  size: number;
  mimetype: "video/mp4" | "image/jpeg" | "image/png" | "video/mov";
}

interface AmenityLightboxProps {
  mediaFiles: MediaFile[];
  initialIndex: number;
  onClose: () => void;
}

const AmenityLightbox: React.FC<AmenityLightboxProps> = ({
  mediaFiles,
  initialIndex,
  onClose,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Update current index when initialIndex changes
  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  // Auto-scroll thumbnail strip to show active thumbnail
  useEffect(() => {
    const thumbnailElement = document.getElementById(`thumbnail-${currentIndex}`);
    if (thumbnailElement) {
      thumbnailElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [currentIndex]);

  const currentMedia = mediaFiles[currentIndex];

  // Navigate to next media
  const handleNext = useCallback((e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (isTransitioning) return;

    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev + 1) % mediaFiles.length);

    setTimeout(() => setIsTransitioning(false), 300);
  }, [mediaFiles.length, isTransitioning]);

  // Navigate to previous media
  const handlePrev = useCallback((e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (isTransitioning) return;

    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev - 1 + mediaFiles.length) % mediaFiles.length);

    setTimeout(() => setIsTransitioning(false), 300);
  }, [mediaFiles.length, isTransitioning]);

  // Navigate to specific media by index
  const handleThumbnailClick = useCallback((index: number) => {
    if (index === currentIndex || isTransitioning) return;

    setIsTransitioning(true);
    setCurrentIndex(index);

    setTimeout(() => setIsTransitioning(false), 300);
  }, [currentIndex, isTransitioning]);

  // Touch/swipe handlers for mobile devices
  const minSwipeDistance = 50; // Minimum distance in pixels to trigger swipe

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchEnd(null); // Reset touch end
    setTouchStart(e.targetTouches[0].clientX);
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  }, []);

  const onTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd || isTransitioning) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleNext();
    }
    if (isRightSwipe) {
      handlePrev();
    }

    // Reset touch positions
    setTouchStart(null);
    setTouchEnd(null);
  }, [touchStart, touchEnd, isTransitioning, handleNext, handlePrev]);

  // Keyboard navigation support
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNext, handlePrev, onClose]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  if (!mediaFiles.length || !currentMedia) return null;

  const isImage = currentMedia.mimetype === 'image/jpeg' || currentMedia.mimetype === 'image/png';

  // Right chevron (reusing LeftChevron with transform)
  const RightChevron = () => (
    <div className={styles.rightChevron}>
      <LeftChevron />
    </div>
  );

  return (
    <>
      {/* Backdrop */}
      <div
        className={styles.backdrop}
        onClick={onClose}
        aria-label="Close lightbox"
      />

      {/* Lightbox Container */}
      <div className={styles.lightboxContainer}>
        {/* Main Content Area */}
        <div className={styles.mainContent}>
          {/* Close Button */}
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            <CrossIcon size={24} color="#000000" />
          </button>

          {/* Media Display */}
          <div
            className={styles.mediaDisplay}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <div className={`${styles.mediaWrapper} ${isTransitioning ? styles.transitioning : ''}`}>
              {/* Previous Button - positioned relative to mediaWrapper */}
              {mediaFiles.length > 1 && (
                <button
                  className={`${styles.navButton} ${styles.prevButton}`}
                  onClick={handlePrev}
                  aria-label="Previous"
                  type="button"
                  disabled={isTransitioning}
                >
                  <LeftChevron />
                </button>
              )}

              {/* Next Button - positioned relative to mediaWrapper */}
              {mediaFiles.length > 1 && (
                <button
                  className={`${styles.navButton} ${styles.nextButton}`}
                  onClick={handleNext}
                  aria-label="Next"
                  type="button"
                  disabled={isTransitioning}
                >
                  <RightChevron />
                </button>
              )}
              {isImage ? (
                <img
                  src={currentMedia.url}
                  alt={`Amenity media ${currentIndex + 1}`}
                  className={styles.media}
                  loading="eager"
                />
              ) : (
                <video
                  src={currentMedia.url}
                  controls
                  className={styles.media}
                  autoPlay
                  muted={false}
                  playsInline
                  key={currentIndex} // Force re-render for smooth transition
                >
                  <source src={currentMedia.url} type={currentMedia.mimetype} />
                  Your browser does not support the video tag.
                </video>
              )}
            </div>

            {/* Media Counter */}
            {/* {mediaFiles.length > 1 && (
              <div className={styles.mediaCounter}>
                {currentIndex + 1} / {mediaFiles.length}
              </div>
            )} */}

            {/* Thumbnail Strip - moved here to be near media */}
            {mediaFiles.length > 1 && (
              <div className={styles.thumbnailStrip}>
                <div className={styles.thumbnailContainer}>
                  {mediaFiles.map((media, index) => {
                    const isThumbnailImage = media.mimetype === 'image/jpeg' || media.mimetype === 'image/png';
                    const isActive = index === currentIndex;

                    return (
                      <button
                        key={`thumb-${index}-${media.url}`}
                        id={`thumbnail-${index}`}
                        className={`${styles.thumbnail} ${isActive ? styles.activeThumbnail : ''}`}
                        onClick={() => handleThumbnailClick(index)}
                        type="button"
                        aria-label={`View media ${index + 1}`}
                        aria-current={isActive ? 'true' : 'false'}
                      >
                        {isThumbnailImage ? (
                          <img
                            src={media.url}
                            alt={`Thumbnail ${index + 1}`}
                            className={styles.thumbnailImage}
                          />
                        ) : (
                          <div className={styles.videoThumbnail}>
                            <video
                              src={media.url}
                              className={styles.thumbnailVideo}
                              preload="metadata"
                            />
                            <div className={styles.playIcon}>▶</div>
                          </div>
                        )}
                        {isActive && <div className={styles.activeIndicator} />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AmenityLightbox;

